import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import InvoiceDetailModal from "./InvoiceDetailModal.js";
import ManualInvoiceModal from "../Invoicing/CreateManualInvoice.js";
import EditInvoiceModal from "../Invoicing/EditInvoiceModal.js";
import api from "../../services/api.js";
import AddCustomerTransaction from "../Transaction/AddCustomerTransaction.js";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

export default function CustomerInvoiceView({ role, cust_id, token }) {
  const [invoiceList, setInvoiceList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openEditInvoice, setOpenEditInvoice] = useState(false);
  const [sendingInvoiceId, setSendingInvoiceId] = useState(null);
  const [deletingInvoiceId, setDeletingInvoiceId] = useState(null);
  const [openAddTransaction, setOpenAddTransaction] = useState(false);
  const [selectedInvoiceForTransaction, setSelectedInvoiceForTransaction] =
    useState(null);

  // Helper: Format Rupiah
  const formatRupiah = (num) => {
    if (!num) return "0";
    return parseInt(num).toLocaleString("id-ID");
  };

  // Helper: Format Date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Helper: Status Badge
  const statusBadge = (status) => {
    const base = "px-2 py-1 rounded text-xs font-semibold";
    switch (status) {
      case "Paid":
        return (
          <span className={`${base} bg-green-100 text-green-700`}>PAID</span>
        );
      case "Unpaid":
        return (
          <span className={`${base} bg-red-100 text-red-700`}>UNPAID</span>
        );
      case "Cancelled":
        return (
          <span className={`${base} bg-gray-200 text-gray-700`}>CANCELLED</span>
        );
      default:
        return (
          <span className={`${base} bg-gray-100 text-gray-600`}>{status}</span>
        );
    }
  };

  // Function Fetch Data (Dibuat reusable)
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/invoice/${cust_id}`, {
        headers: { Authorization: token },
      });

      // 🔥 FIX UTAMA DI SINI
      setInvoiceList(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching invoices:", err);
      setInvoiceList([]); // safety
    } finally {
      setLoading(false);
    }
  }, [cust_id, token]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleSendInvoiceEmail = async (invoiceId) => {
    if (!invoiceId) return;

    const confirmSend = window.confirm("Send invoice email to customer?");

    if (!confirmSend) return;

    try {
      setSendingInvoiceId(invoiceId);

      const res = await api.get(`/send-inv-email-by-id/${invoiceId}`, {
        headers: { Authorization: token },
      });

      alert(`Invoice email sent to ${res.data.email}`);
    } catch (err) {
      console.error("Send invoice email error:", err);

      alert(err.response?.data?.message || "Failed to send invoice email");
    } finally {
      setSendingInvoiceId(null);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!invoiceId) return;

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this invoice? This action cannot be undone."
    );

    if (!confirmDelete) return;

    try {
      setDeletingInvoiceId(invoiceId);

      await api.delete(`/delete-invoice/${invoiceId}`, {
        headers: { Authorization: token },
      });

      alert("Invoice berhasil dihapus");

      // Refresh list setelah delete
      fetchInvoices();
    } catch (err) {
      console.error("Delete invoice error:", err);
      alert(err.response?.data?.error || "Gagal menghapus invoice");
    } finally {
      setDeletingInvoiceId(null);
    }
  };

  return (
    <>
      {/* HEADER SECTION */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-1.5 h-5 bg-orange-500 rounded-full"></div>
        <h2 className="text-xl font-bold dark:text-white">Invoices</h2>
      </div>

      {/* create invoice button */}
      <div className="flex flex-wrap gap-3 mb-4">
        {role === "billing" && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 ml-auto rounded shadow transition-colors"
          >
            Create Invoice
          </button>
        )}

        <ManualInvoiceModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => {
            console.log("Invoice created, refreshing data...");
            fetchInvoices(); // Refresh list otomatis setelah simpan
          }}
        />
      </div>

      {/* CONTENT SECTION */}
      {loading ? (
        <p className="text-gray-500 dark:text-gray-400">Loading invoices...</p>
      ) : !invoiceList.length ? (
        <div className="p-8 border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-lg text-center">
          <p className="text-red-500 dark:text-red-300">
            No invoices found for this customer.
          </p>
        </div>
      ) : (
        <motion.div
          className="w-full overflow-x-auto"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300">
                  <th className="p-3 border-b dark:border-gray-700 text-sm">
                    Date
                  </th>
                  <th className="p-3 border-b dark:border-gray-700 text-sm">
                    Description
                  </th>
                  <th className="p-3 border-b dark:border-gray-700 text-sm">
                    Payment Method
                  </th>
                  <th className="p-3 border-b dark:border-gray-700 text-sm">
                    Amount (IDR)
                  </th>
                  <th className="p-3 border-b dark:border-gray-700 text-sm">
                    Status
                  </th>
                  {role === "billing" && (
                    <th className="p-3 border-b dark:border-gray-700 text-center-sm">
                      Options
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {invoiceList.map((inv) => (
                  <tr
                    key={inv.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                  >
                    <td className="p-3 border-b dark:border-gray-700">
                      <span className="text-sm">{formatDate(inv.date)}</span>
                      {inv.datepaid && (
                        <div className="text-[10px] text-green-500 font-medium uppercase">
                          Paid: {formatDate(inv.datepaid)}
                        </div>
                      )}
                    </td>

                    <td className="p-3 border-b dark:border-gray-700">
                      <div className="text-sm font-medium dark:text-gray-200">
                        {inv.notes || `Invoice ${inv.invoicenum}`}
                      </div>
                      <div className="text-[11px] text-gray-500">
                        {inv.firstname} {inv.lastname} (ID: {inv.cust_id})
                      </div>
                    </td>

                    <td className="p-3 border-b dark:border-gray-700 text-sm dark:text-gray-400">
                      {inv.paymentmethod || "-"}
                    </td>

                    <td
                      className={`p-3 border-b dark:border-gray-700 font-bold text-sm ${
                        inv.status === "Unpaid"
                          ? "text-red-600"
                          : "text-green-600"
                      }`}
                    >
                      {formatRupiah(inv.total)}
                    </td>

                    <td className="p-3 border-b dark:border-gray-700">
                      {statusBadge(inv.status)}
                    </td>

                    {role === "billing" && (
                      <td className="p-3 border-b dark:border-gray-700 text-center">
                        <Menu
                          as="div"
                          className="relative inline-block text-left"
                        >
                          <MenuButton className="inline-flex items-center gap-x-1 rounded-md bg-gray-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-700">
                            Options
                            <ChevronDownIcon className="h-4 w-4 text-gray-300" />
                          </MenuButton>

                          <MenuItems className="absolute right-0 z-20 mt-2 w-44 origin-top-right rounded-md bg-gray-800 shadow-lg ring-1 ring-white/10 focus:outline-none">
                            <div className="py-1 text-sm text-gray-300">
                              {/* Send Email */}
                              <MenuItem>
                                <button
                                  onClick={() => handleSendInvoiceEmail(inv.id)}
                                  disabled={sendingInvoiceId === inv.id}
                                  className="w-full text-left px-4 py-2 hover:bg-white/5 disabled:opacity-50"
                                >
                                  {sendingInvoiceId === inv.id
                                    ? "Sending..."
                                    : "Send Email"}
                                </button>
                              </MenuItem>

                              {/* Add Transaction */}
                              <MenuItem>
                                <button
                                  disabled={inv.status === "Paid"}
                                  onClick={() => {
                                    setSelectedInvoiceForTransaction(inv);
                                    setOpenAddTransaction(true);
                                  }}
                                  className="w-full text-left px-4 py-2 hover:bg-white/5 disabled:opacity-50"
                                >
                                  Add Transaction
                                </button>
                              </MenuItem>

                              {/* View Invoice */}
                              <MenuItem>
                                <button
                                  onClick={() => setSelectedInvoice(inv)}
                                  className="w-full text-left px-4 py-2 hover:bg-white/5"
                                >
                                  View
                                </button>
                              </MenuItem>

                              {/* Update Invoice */}
                              <MenuItem>
                                <button
                                  disabled={inv.status === "Paid"}
                                  onClick={() => {
                                    setSelectedInvoice(inv);
                                    setOpenEditInvoice(true);
                                  }}
                                  className="w-full text-left px-4 py-2 hover:bg-white/5 disabled:opacity-50"
                                >
                                  Update
                                </button>
                              </MenuItem>

                              {/* Divider */}
                              <div className="my-1 h-px bg-white/10" />

                              {/* Delete */}
                              <MenuItem>
                                <button
                                  disabled={
                                    inv.status === "Paid" ||
                                    deletingInvoiceId === inv.id
                                  }
                                  onClick={() => handleDeleteInvoice(inv.id)}
                                  className="w-full text-left px-4 py-2 text-red-400 hover:bg-red-500/10 disabled:opacity-50"
                                >
                                  {deletingInvoiceId === inv.id
                                    ? "Deleting..."
                                    : "Delete"}
                                </button>
                              </MenuItem>
                            </div>
                          </MenuItems>
                        </Menu>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* MODAL DETAIL */}
      <InvoiceDetailModal
        invoice={selectedInvoice}
        onClose={() => setSelectedInvoice(null)}
      />

      <EditInvoiceModal
        open={openEditInvoice}
        onClose={() => setOpenEditInvoice(false)}
        invoiceId={selectedInvoice?.id}
        selectedInvoice={selectedInvoice}
        token={token}
        onSuccess={fetchInvoices}
      />

      <AddCustomerTransaction
        open={openAddTransaction}
        onClose={() => {
          setOpenAddTransaction(false);
          setSelectedInvoiceForTransaction(null);
        }}
        token={token}
        cust_id={cust_id} // userid dari parent
        presetInvoiceId={selectedInvoiceForTransaction?.id}
        presetAmount={selectedInvoiceForTransaction?.total}
        onSuccess={() => {
          fetchInvoices(); // refresh invoice setelah transaksi ditambahkan
        }}
      />
    </>
  );
}
